import * as React from 'react';
import * as Moment from 'moment';
import { Form, useFormApi } from 'informedv4';
import { TextBox } from '../forms/informedv4/TextBoxV4';
import AsyncActionButton from '../misc/components/AsyncActionButton';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { Checkbox } from '../forms/informedv4/CheckboxV4';
import { ConfigFieldTextBox } from '../forms/informedv4/ConfigFieldTextBoxV4';
import { ConfigFieldDatePicker } from '../forms/informedv4/ConfigFieldDatePickerV4';
import { ConfigurableDataItemIds } from '../misc/api/ApiConfigurableDataItems';
import { useWebPortalApiClient } from '../misc/api/WebPortalTokenClient';
import { DatePickerAcceptDateRange } from '../forms/informedv4/DatePickerV4';

export interface RandomizationModalBodyProps {
    studyId: string;
    subjectId: string;
    initialValues: RandomizationForm;
    onClose: () => void;
    onSubmit: (values: RandomizationForm) => void;
}

export interface RandomizationForm {
    SubjectNumber: string;
    IsRandomized: boolean;
    RandomizationNumber: string;
    RandomizationDate: string;
}

const RandomizationModalBodyForm = ({ studyId, subjectId, initialValues, onClose, onSubmit, children}: React.PropsWithChildren<RandomizationModalBodyProps>) => {
    const formApi = useFormApi();
    const [checked, setChecked] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const client = useWebPortalApiClient();

    const formatString =  "YYYY-MM-DD HH:mm";

    React.useEffect(() => {
        setChecked(initialValues.IsRandomized);
    }, []);

    React.useEffect(() => {
        if (formApi && checked) {
            if (formApi.getValue("RandomizationDate"))
                formApi.setValue('RandomizationDate', Moment(initialValues.RandomizationDate).format(formatString));
            else
                formApi.setValue('RandomizationDate', Moment(new Date()).format(formatString));
        }
    }, [checked, formApi]);

    const validateRandomizedNumber = async (subjectNumber: string, randomizedNumber: string) => {
        const subjects = await client.client.getSubjects("id", `(site/study/id eq ${studyId}) AND (number ne '${subjectNumber}') AND (randomizationNumber eq '${randomizedNumber}')`)
        if (subjects.value.length > 0) {
            formApi.setError("RandomizationNumber", "Randomization Id already exists");
        }
    }

    const handleSubmit = async () => {
        formApi.validate();
        let state = formApi.getFormState();

        let proposedRandomizationNumber: string = state.values.RandomizationNumber as string;
        
        setSubmitting(true);
        if (state.values.IsRandomized) {
            if (proposedRandomizationNumber === undefined || proposedRandomizationNumber === null) {
                proposedRandomizationNumber = state.values.SubjectNumber as string;
            }

            await validateRandomizedNumber(state.values.SubjectNumber as string, proposedRandomizationNumber);
            if (Moment(state.values.RandomizationDate) > Moment(new Date())){
                formApi.setError("RandomizationDate", "Selected Randomization date must less than today's date");
            }

            setSubmitting(false);
        }

        
            
        state = formApi.getFormState();

        if (state.invalid)
            return;

        onSubmit({
            RandomizationNumber: proposedRandomizationNumber,
            RandomizationDate: Moment(state.values.RandomizationDate).format("YYYY-MM-DDTHH:mm:ssZ"),
            IsRandomized: state.values.IsRandomized as boolean,
            SubjectNumber: state.values.SubjectNumber as string,
        });
        setSubmitting(false);
    }

    return (
        <div>
            <ErrorBoundary>
                <div className="modal-body">
                    {children}
                    <div className="form-group">
                        <label htmlFor="subjectNumber">Subject Number</label>
                        <TextBox name="SubjectNumber" id="SubjectNumber" field="SubjectNumber" type="text" disabled />
                    </div>

                    <div className="form-group">
                        <Checkbox name="IsRandomized" label="Randomized" id="IsRandomized" field="IsRandomized" onChange={(c) => setChecked(c)} />
                    </div>

                    <ConfigFieldTextBox
                        name="RandomizationNumber"
                        id="RandomizationNumber"
                        field="RandomizationNumber"
                        className="form-group"
                        hidden={!checked}
                        validated={checked}
                        required={checked}
                        itemId={ConfigurableDataItemIds.RandomizationNumber}
                    />

                    {checked && <ConfigFieldDatePicker
                        name="RandomizationDate"
                        id="RandomizationDate"
                        field="RandomizationDate"
                        className="form-group"
                        required={checked}
                        includeTime={true}
                        acceptDateRange={DatePickerAcceptDateRange.PastOrPresent}
                        itemId={ConfigurableDataItemIds.RandomizationDate}
                    />}   
                </div>

                <div className="modal-footer">
                    <AsyncActionButton
                        className="btn btn-primary"
                        type="submit"
                        isSubmitting={submitting}
                        onClick={handleSubmit}
                    >
                        Ok
                        </AsyncActionButton>
                    <button className="btn btn-outline btn-primary" onClick={onClose}>Cancel</button>
                </div>
            </ErrorBoundary>
        </div>
    );
}

export const RandomizationModalBody = (props: React.PropsWithChildren<RandomizationModalBodyProps>) => {
    return (
        <div>
            <Form validateOn="submit" initialValues={props.initialValues as unknown as Record<string, unknown>}>
                <RandomizationModalBodyForm  {...props} />
            </Form>
        </div>
    );
}
